import type { QsoType } from '@/types';

/**
 * Normalization field constants
 *
 * These define which QSO fields should be normalized (case-converted) during:
 * 1. Database persistence (utils/normalizeQso.ts)
 * 2. Duplicate detection (models/Logger.ts)
 *
 * Single source of truth for normalization rules.
 */

/**
 * Fields that should be uppercased during normalization
 * Used for callsigns, park/summit references, and modes
 */
export const UPPERCASE_NORMALIZATION_FIELDS: ReadonlyArray<keyof QsoType> = [
  'call',
  'operator',
  'myPotaRef',
  'potaRef',
  'sotaRef',
  'mySotaRef',
  'myState',
  'mode'
] as const;

/**
 * Fields that should be lowercased during normalization
 * Used for band designations (e.g., "20m" not "20M")
 */
export const LOWERCASE_NORMALIZATION_FIELDS: ReadonlyArray<keyof QsoType> = [
  'band'
] as const;

/**
 * Set version of UPPERCASE_NORMALIZATION_FIELDS for O(1) lookups
 * Used in performance-critical duplicate detection
 */
export const UPPERCASE_NORMALIZATION_FIELDS_SET = new Set(UPPERCASE_NORMALIZATION_FIELDS);

/**
 * Set version of LOWERCASE_NORMALIZATION_FIELDS for O(1) lookups
 * Used in performance-critical duplicate detection
 */
export const LOWERCASE_NORMALIZATION_FIELDS_SET = new Set(LOWERCASE_NORMALIZATION_FIELDS);

/**
 * Fields that downstream code treats as strings (calls .trim(), .includes(),
 * .toUpperCase() on them) but that may be persisted as non-strings.
 *
 * **Why:** Until v2.48.x, `fast-xml-parser` was constructed with its default
 * `parseTagValue: true` for QRZ/HamQTH lookups. That silently coerced
 * numeric-looking XML tag content (e.g. `<state>64</state>` for French dept.
 * 64) into JS numbers, and those numbers were persisted to PouchDB as-is.
 * Reading such a record and calling `state.trim()` throws TypeError.
 *
 * The parser is fixed now, but the read-boundary coercion (in
 * `QsoPouchRepo` via `coerceQsoTextFields`) and the write-side step (in
 * `normalizeQso`) are intentionally permanent, not transitional. Reasons we
 * are *not* migrating PouchDB to retroactively fix the data:
 *  - HAMRS is local-first; there's no central DB to migrate. A blocking
 *    startup migration would run on every device with no telemetry to
 *    confirm success and no rollback if it fails halfway.
 *  - Even with a migration, sync conflicts, ADIF re-imports, or backup
 *    restores can re-introduce corrupt records at any time. The read-
 *    boundary coercion is load-bearing regardless.
 *  - Coercion at read is idempotent, self-healing, microsecond-cheap, and
 *    has zero risk of making the data worse. A migration can.
 *
 * **Derivation:** any field on AdifRecordType / QsoType that downstream code
 * invokes a string method on. Grep for `.trim()`, `.includes(`,
 * `.toUpperCase(`, `.toLowerCase(` on `fields.*` to extend this list.
 */
export const STRING_COERCION_FIELDS: ReadonlyArray<keyof QsoType> = [
  'state',
  'myState',
  'cnty',
  'myCnty',
  'country',
  'myCountry',
  'qth',
  'gridsquare',
  'myGridsquare',
] as const;
